<template>
  <v-dialog v-model="open" persistent no-click-animation @keydown.esc="close" max-width="820px">
    <v-form ref="form" v-model="valid" lazy-validation>
      <v-card>
        <v-card-title>
          <v-icon color="primary" class="me-2">mdi-account-plus</v-icon>
          <span class="text-h5">{{ $t('add_parent') }}</span>
        </v-card-title>

        <v-card-subtitle class="pt-2">
          {{ $t('add_parent_hint') }}
        </v-card-subtitle>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="parentName"
                  :rules="nameRules"
                  :label="$t('parent_name') + '*'"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="parentEmail"
                  :rules="emailRules"
                  :label="$t('parent_email') + '*'"
                  :hint="$t('parent_email_hint')"
                  persistent-hint
                  required
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row>
              <!-- the password is set here and handed over, rather than mailed
                   as a link that cannot be checked on -->
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  :label="$t('Password') + '*'"
                  :hint="$t('parent_password_hint')"
                  persistent-hint
                  :append-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  :type="showPassword ? 'text' : 'password'"
                  required
                  @click:append="showPassword = !showPassword"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <!-- a parent is only visible to the school through their students,
                 so at least one student has to be entered here -->
            <div
              v-for="(student, index) in students"
              :key="index"
              class="mb-2"
            >
              <v-row align="center">
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="student.name"
                    :rules="nameRules"
                    :label="$t('student_name') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="3">
                  <v-text-field
                    v-model="student.identification"
                    :rules="nameRules"
                    :label="$t('student_id') + '*'"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="student.notes"
                    :rules="nameRules"
                    :label="$t('notes') + '*'"
                    :hint="$t('student_notes_hint')"
                    required
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="1">
                  <v-icon
                    v-if="students.length > 1"
                    small
                    @click="removeStudent(index)"
                  >
                    mdi-close-circle
                  </v-icon>
                </v-col>
              </v-row>
            </div>

            <!-- the upload endpoint accepts up to four students per parent row -->
            <v-btn
              v-if="students.length < 4"
              text
              small
              color="primary"
              @click="addStudent"
            >
              <v-icon small class="me-1">mdi-plus</v-icon>
              {{ $t('add_another_student') }}
            </v-btn>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text :disabled="isSaving" @click="close">
            {{ $t('close') }}
          </v-btn>
          <v-btn color="blue darken-1" text :loading="isSaving" @click="submit">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
/*
 * Adds a parent together with their first student.
 *
 * There is no endpoint that creates a lone parent, and a parent with no student
 * would not appear in this school's list at all -- the list is derived from the
 * students attached to the school. So each child is posted to
 * /school-students/add, which creates the parent and their Firebase account on
 * the first call, with the password the school chose, and attaches the rest to
 * the parent it now finds.
 */

export default {
  name: "AddParentDialog",
  props: {
    value: { type: Boolean, default: false },
  },
  data() {
    return {
      valid: true,
      isSaving: false,
      parentName: "",
      parentEmail: "",
      password: "",
      showPassword: false,
      students: [{ name: "", identification: "", notes: "" }],
      nameRules: [(v) => !!v || this.$t("required_field")],
      emailRules: [
        (v) => !!v || this.$t("E-mail is required"),
        (v) => /.+@.+\..+/.test(v) || this.$t("E-mail must be valid"),
      ],
      passwordRules: [
        (v) => !!v || this.$t("password_required"),
        (v) => (v && v.length >= 6) || this.$t("password_min_length"),
      ],
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.reset();
    },
  },
  methods: {
    reset() {
      this.parentName = "";
      this.parentEmail = "";
      this.password = "";
      this.showPassword = false;
      this.students = [{ name: "", identification: "", notes: "" }];
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
    addStudent() {
      this.students.push({ name: "", identification: "", notes: "" });
    },
    removeStudent(index) {
      this.students.splice(index, 1);
    },
    close() {
      this.open = false;
    },

    /*
     * One request per child, and none of them carries a spreadsheet.
     *
     * This dialog used to build an .xlsx in the browser - half a megabyte of
     * library, fetched to serve a form with four boxes - post it to the bulk
     * importer, and then make a second request to replace the default password
     * the importer had just assigned. Between those two requests the new
     * parent existed with a password of 12345678, and if the second one failed
     * they kept it.
     *
     * /school-students/add takes the parent and one child as plain JSON and
     * sets the chosen password while creating the account. The parent is
     * created by the first call and found by the rest, so a family of three is
     * three small requests and no library at all.
     */
    async submit() {
      if (!this.$refs.form.validate()) return;

      const email = this.parentEmail.trim();
      const students = this.students.filter(
        (student) => student.name.trim() || student.identification.trim()
      );

      this.isSaving = true;

      try {
        for (let i = 0; i < students.length; i++) {
          await axios.post("/school-students/add", {
            name: students[i].name.trim(),
            student_identification: students[i].identification.trim(),
            notes: students[i].notes.trim() || null,
            parent_email: email,
            parent_name: this.parentName.trim(),
            //only the call that creates the parent uses it; the rest find them
            parent_password: i === 0 ? this.password : undefined,
          });
        }

        this.$notifySuccess(this.$t("parent_created", { email: email }));
        this.open = false;
        this.$emit("created");
      } catch (error) {
        this.$notifyError(error, this.$t("error_creating_parent"));
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>
